import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateCompanyInput, CreateCompanyInputSchema } from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { Switch } from '@/shared/ui/switch'

type CompanyFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateCompanyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: CreateCompanyInput) => void
  onCancel: () => void
}

/**
 * Formulario de empresa (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema (@idc-forms/schemas) que valida el backend. El `type`
 * (IDC/CVN) es de sistema y no se edita.
 */
export function CompanyForm({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: CompanyFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateCompanyInput>({
    resolver: zodResolver(CreateCompanyInputSchema),
    defaultValues: {
      default: false,
      active: true,
      ...defaultValues
    }
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="company-name">Nombre</FieldLabel>
                <Input
                  id="company-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Razón social completa, única entre empresas.</FieldDescription>
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="giinNumber"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.giinNumber)}>
                <FieldLabel htmlFor="company-giin">Número GIIN</FieldLabel>
                <Input
                  id="company-giin"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>
                  Identificador FATCA (opcional), p.ej. AEALNI.99999.SL.320.
                </FieldDescription>
                <FieldError errors={[errors.giinNumber]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Estado</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="default"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="company-default">Predeterminada</FieldLabel>
                <Switch
                  id="company-default"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
          <FieldDescription>
            Solo puede haber una empresa predeterminada: marcarla desmarca la actual y la deja
            activa.
          </FieldDescription>

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="company-active">Activa</FieldLabel>
                <Switch
                  id="company-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
